/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useEffect, useRef, useState } from 'react'
import { sendCheer } from '@sportninja/common/services/game/cheerService'
import colors from '@sportninja/common/constants/appColors'
const EMOJI_MAP: Record<string, string> = {
  ':clap:': '\u{1F44F}',
}

const resolveEmoji = (code: string): string => EMOJI_MAP[code] || '\u{1F44F}'

const ANIMATION_DURATION = 1500
const MAX_POOL_SIZE = 10

interface CheerEvent {
  id: string
  emoji: string
  team: string
  receivedAt: number
}

interface CheersButtonProps {
  gameId: string
  cheerEvents: CheerEvent[]
  onCheerAnimationComplete: (id: string) => void
}

interface SlotState {
  emoji: string
  active: boolean
  seed: number
  eventId: string | null
}

const createEmptySlot = (): SlotState => ({
  emoji: ':clap:',
  active: false,
  seed: 0,
  eventId: null,
})

const floatUp = css`
  @keyframes cheerFloat {
    0% {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
    60% {
      transform: translateY(-120px) scale(1.15);
      opacity: 1;
    }
    100% {
      transform: translateY(-200px) scale(1.15);
      opacity: 0;
    }
  }
`

const AnimatedSlot = ({
  slotState,
  onAnimationEnd,
}: {
  slotState: SlotState & { xOffset: number }
  onAnimationEnd: () => void
}) => {
  if (!slotState.active) {
    return null
  }

  return (
    <span
      key={slotState.seed}
      onAnimationEnd={onAnimationEnd}
      css={css`
        ${floatUp};
        position: absolute;
        bottom: 100%;
        left: 50%;
        margin-left: -12px;
        font-size: 24px;
        pointer-events: none;
        animation: cheerFloat ${ANIMATION_DURATION}ms ease-out forwards;
        transform-origin: center;
        transform: translateX(${slotState.xOffset}px);
      `}
    >
      {resolveEmoji(slotState.emoji)}
    </span>
  )
}

const CheersButton = ({
  gameId,
  cheerEvents,
  onCheerAnimationComplete,
}: CheersButtonProps) => {
  const [slots, setSlots] = useState<(SlotState & { xOffset: number })[]>(() =>
    Array.from({ length: MAX_POOL_SIZE }, () => ({
      ...createEmptySlot(),
      xOffset: 0,
    }))
  )

  const processedIdsRef = useRef(new Set<string>())

  const assignSlot = (emoji: string, eventId: string | null) => {
    setSlots((prev) => {
      const freeIndex = prev.findIndex((s) => !s.active)
      const targetIndex = freeIndex === -1 ? 0 : freeIndex
      const xOffset = (Math.random() - 0.5) * 20
      const next = [...prev]
      next[targetIndex] = {
        emoji,
        active: true,
        seed: prev[targetIndex].seed + 1,
        eventId,
        xOffset,
      }
      return next
    })
  }

  const handleSlotDone = (index: number) => {
    setSlots((prev) => {
      const slot = prev[index]
      if (slot.eventId && !slot.eventId.startsWith('local-')) {
        onCheerAnimationComplete(slot.eventId)
      }
      const next = [...prev]
      next[index] = { ...slot, active: false, eventId: null, xOffset: 0 }
      return next
    })
  }

  useEffect(() => {
    for (const event of cheerEvents) {
      if (!processedIdsRef.current.has(event.id)) {
        processedIdsRef.current.add(event.id)
        assignSlot(event.emoji, event.id)
      }
    }
  }, [cheerEvents])

  useEffect(() => {
    if (processedIdsRef.current.size > 200) {
      processedIdsRef.current.clear()
    }
  }, [cheerEvents])

  const stopEvent = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const SEND_THROTTLE_MS = 300
  const lastSendAtRef = useRef(0)

  const handleClick = (e: React.MouseEvent) => {
    stopEvent(e)
    assignSlot(':clap:', `local-${Date.now()}`)

    const now = Date.now()
    if (now - lastSendAtRef.current < SEND_THROTTLE_MS) {
      return
    }
    lastSendAtRef.current = now
    sendCheer(gameId).catch(() => {
      /* ignore */
    })
  }

  return (
    <div
      onDoubleClick={(e) => e.stopPropagation()}
      css={css`
        position: absolute;
        bottom: 70px;
        right: 16px;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
    >
      {slots.map((slot, i) => (
        <AnimatedSlot
          key={i}
          slotState={slot}
          onAnimationEnd={() => handleSlotDone(i)}
        />
      ))}

      <button
        onClick={handleClick}
        onDoubleClick={stopEvent}
        css={css`
          background-color: rgba(0, 0, 0, 0.6);
          border-radius: 8px;
          border: 1px solid ${colors.NEUTRAL_100};
          width: 40px;
          height: 40px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          line-height: 1;

          &:hover {
            background-color: rgba(0, 0, 0, 0.8);
          }
        `}
      >
        {'\u{1F44F}'}
      </button>
    </div>
  )
}

export default CheersButton
